import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../index";
import { Loader } from "@googlemaps/js-api-loader";
import _locations from "./locations.json";
import { ReactComponent as DistanceIcon } from "../assets/globe-solid.svg";
import { ReactComponent as LocationIcon } from "../assets/location-dot-solid.svg";
import { ReactComponent as PhoneIcon } from "../assets/icon_phone-black.svg";
import { ReactComponent as MailIcon } from "../assets/icon_mail-black.svg";
import AutoCompleteWrapper from "./AutocompleteWrapper";

interface Location {
  name: string;
  location: {
    latitude: number;
    longitude: number;
  };
  address: string;
  phone: string;
  email: string;
  website: string;
  distanceFromOrigin?: number;
}

function pinSvgString(num: number, size = "56", fill = "#00AAFF"): string {
  return `<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 85.04 117.41"
  width="${size}"
  height="${size}">
    <path
    d="m42.52,0C19.07,0,0,19.07,0,42.52c0,29.1,38.05,71.81,39.67,73.62,1.52,1.69,4.18,1.69,5.7,0,1.62-1.8,39.67-44.52,39.67-73.62,0-23.45-19.07-42.52-42.52-42.52Zm0,63.91c-11.8,0-21.39-9.6-21.39-21.39s9.6-21.39,21.39-21.39,21.39,9.6,21.39,21.39-9.6,21.39-21.39,21.39Z"
    fill="${fill}"
  />
    <text
    font-size="32"
    font-weight="bold">
        <tspan
      x="42"
      y="54"
      text-anchor="middle">
            ${num}
        </tspan>
    </text>
</svg>`;
}

function vincentyGreatCircleDistance(
  latFrom: number,
  lonFrom: number,
  latTo: number,
  lonTo: number,
  earthRadius = 6371.009
) {
  latFrom = latFrom * (Math.PI / 180);
  lonFrom = lonFrom * (Math.PI / 180);
  latTo = latTo * (Math.PI / 180);
  lonTo = lonTo * (Math.PI / 180);

  const lonDiff = lonTo - lonFrom;
  const a =
    Math.pow(Math.cos(latTo) * Math.sin(lonDiff), 2) +
    Math.pow(Math.cos(latFrom) * Math.sin(latTo) - Math.sin(latFrom) * Math.cos(latTo) * Math.cos(lonDiff), 2);
  const b = Math.sin(latFrom) * Math.sin(latTo) + Math.cos(latFrom) * Math.cos(latTo) * Math.cos(lonDiff);
  const angle = Math.atan2(Math.sqrt(a), b);
  return angle * earthRadius;
}

function calculateAndSortLocations(locations: Location[], center: { lat: number; lng: number }): Location[] {
  return locations
    .map((location) => {
      const distanceFromOrigin = vincentyGreatCircleDistance(
        center.lat,
        center.lng,
        location.location.latitude,
        location.location.longitude
      );
      return { ...location, distanceFromOrigin };
    })
    .sort((a: Location, b: Location) => a.distanceFromOrigin! - b.distanceFromOrigin!);
}

const latLngBounds: google.maps.LatLngBoundsLiteral = {
  north: 60,
  south: 30,
  west: -15,
  east: 21,
};

const parser = new DOMParser();
let markers: any[] = [];
let centerMarker: any;

const locationIcon = parser.parseFromString(
  `<svg
  xmlns="http://www.w3.org/2000/svg"
  width="32"
  height="32"
  fill="#1a1a1a"
  viewBox="0 0 16 16">
    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
</svg>`,
  "image/svg+xml"
).documentElement;

function handleElementRef(refs: React.MutableRefObject<any>, key: number, ref: HTMLLIElement | null) {
  refs.current[key] = ref;
}

async function panAndZoomToCoordinates(
  map: google.maps.Map,
  coordinates: google.maps.LatLngLiteral,
  placeMarker = false
) {
  setTimeout(() => map.setCenter(coordinates), 0);
  map.setZoom(12);
  if (placeMarker) {
    const { AdvancedMarkerElement } = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;
    centerMarker?.setMap(null);
    centerMarker = null;
    centerMarker = new AdvancedMarkerElement({
      map,
      position: coordinates,
      content: locationIcon,
    });
  }
}

function LocationLI({
  location,
  index,
  marked,
  onClick,
  refO,
}: {
  location: Location;
  index: number;
  marked: boolean;
  onClick: any;
  refO: React.MutableRefObject<any>;
}) {
  const color = marked ? "#f5f5f5" : "#0092FF";
  return (
    <li
      className={"location_li"}
      onClick={onClick}
      id={marked ? "marked_entry" : ""}
      ref={(ref) => handleElementRef(refO, index - 1, ref)}>
      <div
        dangerouslySetInnerHTML={{
          __html: pinSvgString(index, "32", color),
        }}
        className="pinIconCol"
      />
      <div className="infoCol">
        <div className="location_title">{location.name}</div>
        <div>
          <DistanceIcon />
          {Math.round(location.distanceFromOrigin || 0)} km
        </div>
          {location.address && (
              <div className="location_address">
                  <LocationIcon />
                  <span>
                    {location.address}
                  </span>
              </div>
          )}
          {location.phone && (
              <div>
                  <a href={`tel:${location.phone}`}>
                      <PhoneIcon />
                      {location.phone}
                  </a>
              </div>
          )}
          {location.email && (
              <div>
                  <a href={`mailto:${location.email}`}>
                      <MailIcon />
                      {location.email}
                  </a>
              </div>
          )}
      </div>
    </li>
  );
}

function resetMarkers() {
  markers.forEach(
    (marker, j) => (marker.content = parser.parseFromString(pinSvgString(j + 1), "image/svg+xml").documentElement)
  );
}

function LocationMap() {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.AutocompleteService | null>(null);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const { googleMapsApiKey, partners, initialMapCenter, initialMapZoom, searchLocation, lang, region } = useContext(AppContext);
  const mapDiv = useRef(null);
  const autoCompleteInput = useRef<HTMLInputElement>(null);
  const submitButton = useRef<HTMLButtonElement>(null);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [markedEntry, setMarkedEntry] = useState(-1);
  const liRefs = useRef<any>({});
  const [index, setIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(false);
  const [mapCenter, setMapCenter] = useState(searchLocation);
    const options: google.maps.MapOptions = {
        disableDefaultUI: true,
        restriction: {
            latLngBounds,
            strictBounds: false,
        },
        center: initialMapCenter,
        zoom: initialMapZoom,
        mapId: "b00f1ec49f545b51",
    };

  useEffect(() => {
    const matchQueryList = window.matchMedia("(max-width: 1000px)");
    setIsMobile(matchQueryList.matches);
    matchQueryList.addEventListener("change", (e) => setIsMobile(e.matches));
  }, []);

  function findLatLng(address: string): Promise<google.maps.LatLngLiteral | boolean> {
    return new Promise(function (resolve, reject) {
      if (geocoder) {
        geocoder
          .geocode({ address: address, bounds: latLngBounds })
          .then(({ results }) => {
            const is_country = results[0].types && results[0].types[0] === "country";
            if (is_country) {
              resolve(false); // location not really found
            } else {
              resolve({ lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() });
            }
          })
          .catch((err) => {
            reject(new Error(`Couldn't find the location ${address}: ${err}`));
          });
      } else {
        reject("Geocoder not loaded yet");
      }
    });
  }

  function setInput(val: string) {
    if (autoCompleteInput.current) {
      autoCompleteInput.current.value = val;
    }
  }

  function submitSearch() {
    if (autoCompleteInput.current) {
      let name;
      if (predictions[index]) {
        name = predictions[index].description;
        if (autoCompleteInput.current) {
          autoCompleteInput.current.value = name;
        }
      } else {
        name = autoCompleteInput.current.value;
      }
      setPredictions([]);

      findLatLng(name).then(async (geo) => {
        if (geo && map && typeof geo !== "boolean") {
          const newCenter = {
            lat: geo.lat ?? searchLocation.lat,
            lng: geo.lng ?? searchLocation.lng,
          };
          await panAndZoomToCoordinates(map, newCenter);
          const { AdvancedMarkerElement } = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;
          centerMarker?.setMap(null);
          centerMarker = null;
          centerMarker = new AdvancedMarkerElement({
            map,
            position: { lat: newCenter.lat, lng: newCenter.lng },
            content: locationIcon,
          });
          setMapCenter(newCenter);
          setMarkedEntry(-1);
          liRefs.current[0]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "start",
          });
        }
      });
    }
  }

  useEffect(() => {
    const element = liRefs.current[markedEntry];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
    }
  }, [markedEntry]);

  const partnerLocations = partners ? partners : _locations;
  let locations: Location[] = useMemo(() => calculateAndSortLocations(partnerLocations, mapCenter), [partnerLocations, mapCenter]);

  useEffect(() => {
    const loader = new Loader({
      apiKey: googleMapsApiKey,
      version: "weekly",
      language: lang,
      region: region,
      libraries: ["places"],
    });
    loader.load().then(
      () => {
        setHasLoaded(true);
      },
      () => {
        setHasLoaded(false);
      }
    );
  }, [googleMapsApiKey, lang, region]);

  useEffect(() => {
    if (mapDiv.current && hasLoaded) {
      (google.maps.importLibrary("maps") as Promise<google.maps.MapsLibrary>).then(async ({ Map }) => {
        if (mapDiv.current) {
          const newMap = new Map(mapDiv.current, options);
          setMap(newMap);
        }
      });
    }
  // eslint-disable-next-line
  }, [mapDiv, hasLoaded]);

  useEffect(() => {
    if (map && hasLoaded) {
      (google.maps.importLibrary("marker") as Promise<google.maps.MarkerLibrary>).then(({ AdvancedMarkerElement }) => {
        markers.forEach((marker) => marker.setMap(null));
        markers = [];
        locations.forEach((location, i) => {
          const pinSvg = parser.parseFromString(pinSvgString(i + 1), "image/svg+xml").documentElement;
          const marker = new AdvancedMarkerElement({
            map,
            position: { lat: location.location.latitude, lng: location.location.longitude },
            content: pinSvg,
          });
          marker.addListener("click", () => {
            resetMarkers();
            setMarkedEntry(i);
            panAndZoomToCoordinates(map, { lat: location.location.latitude, lng: location.location.longitude });
            marker.content = parser.parseFromString(
              pinSvgString(i + 1, "56", "#00AAFF"),
              "image/svg+xml"
            ).documentElement;
          });
          markers = [...markers, marker];
        });
      });
    }
  }, [hasLoaded, map, locations]);

  useEffect(() => {
    if (hasLoaded) {
      setAutocomplete(new google.maps.places.AutocompleteService());
      setGeocoder(new google.maps.Geocoder());
    }
  }, [hasLoaded]);

  return (
    <div className="map_wrapper">
      {isMobile && (
        <AutoCompleteWrapper
          autoCompleteInput={autoCompleteInput}
          setInput={setInput}
          setPredictions={setPredictions}
          autocomplete={autocomplete}
          setIndex={setIndex}
          index={index}
          predictions={predictions}
          submitButton={submitButton}
          submitSearch={submitSearch}
          map={map}
          setMarkedEntry={setMarkedEntry}
          setMapCenter={setMapCenter}
          latLngBounds={latLngBounds}
          panAndZoomToCoordinates={panAndZoomToCoordinates}
        />
      )}
      <div className="sidebar_wrapper">
        <div id="location_list">
          <ol className="location_list_item">
            {locations.map((location, i) => (
              <LocationLI
                location={location}
                index={i + 1}
                marked={i === markedEntry}
                key={i}
                onClick={() => {
                  if (map) {
                    setMarkedEntry(i);
                    panAndZoomToCoordinates(map, { lat: location.location.latitude, lng: location.location.longitude });
                    resetMarkers();
                    const markerIdx = markers.findIndex(
                      (marker) =>
                        marker.position.lat === location.location.latitude &&
                        marker.position.lng === location.location.longitude
                    );
                    if (markerIdx !== -1) {
                      const marker = markers[markerIdx];
                      marker.content = parser.parseFromString(
                        pinSvgString(markerIdx + 1, "56", "#01BEFF"),
                        "image/svg+xml"
                      ).documentElement;
                    }
                  }
                }}
                refO={liRefs}
              />
            ))}
          </ol>
        </div>
        {!isMobile && (
          <AutoCompleteWrapper
            autoCompleteInput={autoCompleteInput}
            setInput={setInput}
            setPredictions={setPredictions}
            autocomplete={autocomplete}
            setIndex={setIndex}
            index={index}
            predictions={predictions}
            submitButton={submitButton}
            submitSearch={submitSearch}
            map={map}
            setMarkedEntry={setMarkedEntry}
            setMapCenter={setMapCenter}
            latLngBounds={latLngBounds}
            panAndZoomToCoordinates={panAndZoomToCoordinates}
          />
        )}
      </div>
      <div
        id="map"
        ref={mapDiv}></div>
    </div>
  );
}

export default React.memo(LocationMap);
